import api from './api';
import type { Recipe, RecipeLine, PaginatedResponse, SizeUnit } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/recipes`;

// ── Payloads ──────────────────────────────────────────────────────────────────

export interface CreateRecipePayload {
  name: string;
  outputInventoryItemId: string;
  outputQuantity: number;
  yieldPercent?: number;
  notes?: string;
}

export interface UpdateRecipePayload {
  name?: string;
  outputQuantity?: number;
  yieldPercent?: number | null;
  notes?: string | null;
}

export interface RecipeLineInput {
  inventoryItemId: string;
  quantity: number;
  sizeUnit?: SizeUnit;
  position?: number;
  notes?: string;
}

export interface ListRecipesParams {
  page?: number;
  pageSize?: number;
  active?: boolean;
}

// ── Service ───────────────────────────────────────────────────────────────────

const recipeService = {
  listRecipes: async (
    venueId: string,
    params: ListRecipesParams = {},
  ): Promise<PaginatedResponse<Recipe>> => {
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  createRecipe: async (
    venueId: string,
    payload: CreateRecipePayload,
  ): Promise<Recipe> => {
    const response = await api.post(base(venueId), payload);
    return response.data;
  },

  getRecipe: async (
    venueId: string,
    recipeId: string,
  ): Promise<{ recipe: Recipe; lines: RecipeLine[] }> => {
    const response = await api.get(`${base(venueId)}/${recipeId}`);
    return response.data;
  },

  updateRecipe: async (
    venueId: string,
    recipeId: string,
    payload: UpdateRecipePayload,
  ): Promise<{ recipe: Recipe; lines: RecipeLine[] }> => {
    const response = await api.patch(`${base(venueId)}/${recipeId}`, payload);
    return response.data;
  },

  deactivateRecipe: async (
    venueId: string,
    recipeId: string,
  ): Promise<Recipe> => {
    const response = await api.delete(`${base(venueId)}/${recipeId}`);
    return response.data;
  },

  listRecipeLines: async (
    venueId: string,
    recipeId: string,
  ): Promise<{ recipe: Recipe; lines: RecipeLine[] }> => {
    const response = await api.get(`${base(venueId)}/${recipeId}/lines`);
    return response.data;
  },

  replaceRecipeLines: async (
    venueId: string,
    recipeId: string,
    lines: RecipeLineInput[],
  ): Promise<{ recipe: Recipe; lines: RecipeLine[] }> => {
    const response = await api.put(`${base(venueId)}/${recipeId}/lines`, { lines });
    return response.data;
  },
};

export default recipeService;
